import { Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { Observable, Subscription, map } from 'rxjs';

import { AppConstants } from '@root/app/app.constants';
import { AppCenterAnalytics, AppInsightsService, CmsService } from '@shared/services';
import { AuthService, MultilingualService, TokenService } from '@core/services';
import { PolicyDetailsDialogComponent } from '../policy-details-dialog/policy-details-dialog.component';

@Component({
    selector: 'app-guest-home',
    templateUrl: './guest-home.component.html',
    styleUrls: ['./guest-home.component.scss'],
    standalone: false
})
export class GuestHomeComponent implements OnInit, OnDestroy {
  @ViewChild('error', { read: TemplateRef }) errorTemplate!: TemplateRef<any>;
  private subscriptions = new Subscription();
  public selectedLanguage!: string;
  public aboutUsLink$!: Observable<string>;

  constructor(
    public cmsService: CmsService,
    public readonly multilingualService: MultilingualService,
    public readonly appInsightsService: AppInsightsService,
    private readonly dialog: MatDialog,
    private readonly snackBar: MatSnackBar,
    private readonly tokenService: TokenService,
    private readonly router: Router,
    private readonly authService: AuthService
  ) {}

  ngOnInit(): void {
    // set provider type as "Guest" for guest screen
    this.tokenService.setProviderType();

    this.appInsightsService.trackPageViewWithoutClientId(
      `${AppCenterAnalytics.GUEST_HOME_VIEWED}`
    );

    // load translations
    this.multilingualService.getLangMap(
      this.multilingualService.getUserLanguage(),
      true
    );
    this.changeTextSize(AppConstants.TEXT_SIZE.SMALL);

    this.selectedLanguage = this.multilingualService.getUserLanguage();

    this.aboutUsLink$ = this.cmsService.getLinks().pipe(
      map((links) => {
        const aboutUs = links.find(
          (x) => x.name.toLowerCase() === AppConstants.LINK_NAMES.ABOUT_US.toLowerCase()
        );
        return aboutUs ? aboutUs.url : '';
      })
    );
  }

  public changeLang(lang: string): void {
    this.appInsightsService.trackPageViewWithoutClientId(
      `${AppCenterAnalytics.GUEST_HOME_CHANGE_LANGUAGE_CLICKED}`
    );

    this.multilingualService.getLangMap(lang);
    this.subscriptions.add(
      this.multilingualService.langMapFailureObservable.subscribe((res) => {
        this.selectedLanguage = this.multilingualService.getUserLanguage();
        this.snackBar.openFromTemplate(this.errorTemplate, {
          duration: 4000,
          verticalPosition: 'top',
          horizontalPosition: 'center',
        });
      })
    );
  }

  OpenFAQDialog(activeTabIndex: number, FAQType: string) {
    this.appInsightsService.trackPageViewWithoutClientId(
      `${FAQType} ${AppCenterAnalytics.GUEST_HOME_FAQ_CLICKED}`
    );
    this.openDetailsDialog('FAQ', activeTabIndex);
  }

  OpenPolicyDialog(type: 'privacy-policy' | 'terms-of-use') {
    if (type === 'privacy-policy') {
      this.appInsightsService.trackPageViewWithoutClientId(
        `${AppCenterAnalytics.GUEST_HOME_PRIVACY_POLICY_CLICKED}`
      );
    } else if (type === 'terms-of-use') {
      this.appInsightsService.trackPageViewWithoutClientId(
        `${AppCenterAnalytics.GUEST_HOME_TERMS_OF_USE_CLICKED}`
      );
    }
    this.openDetailsDialog(type);
  }

  public openDetailsDialog(
    type: 'privacy-policy' | 'terms-of-use' | 'FAQ',
    activeIndex?: number
  ): void {
    this.dialog.open(PolicyDetailsDialogComponent, {
      height: '90vh',
      width: '80vw',
      data: { isOpenedFromGuest: true, type: type, activeIndex },
    });
  }

  public goToLogin() {
    this.appInsightsService.trackPageViewWithoutClientId(
      AppCenterAnalytics.GUEST_HOME_LOGIN_CLICKED
    );
    this.subscriptions.add(
      this.authService
        .checkForB2CActiveAccount()
        .subscribe(() =>
          this.authService.navigateToLoginPage(this.selectedLanguage)
        )
    );
  }

  public createAccount() {
    this.appInsightsService.trackPageViewWithoutClientId(
      AppCenterAnalytics.GUEST_HOME_CREATE_ACCOUNT_CLICKED
    );
    this.router.navigate(['sign-up'], {
      queryParams: { ui_locales: this.selectedLanguage },
    });
  }

  public changeTextSize(textSizeValue: string) {
    let fontSize: string;
    this.multilingualService.selectedTextSize = textSizeValue;

    switch (textSizeValue) {
      case AppConstants.TEXT_SIZE.MEDIUM: {
        fontSize = '18px';
        break;
      }
      case AppConstants.TEXT_SIZE.LARGE: {
        fontSize = '20px';
        break;
      }
      default: {
        fontSize = '16px';
        break;
      }
    }
    document.getElementsByTagName('html')[0].style.fontSize = fontSize;
    const bodyClasses = document.getElementsByTagName('body')[0].classList;
    bodyClasses.remove(`${AppConstants.TEXT_SIZE.LARGE}-text-size`);
    bodyClasses.remove(`${AppConstants.TEXT_SIZE.MEDIUM}-text-size`);
    bodyClasses.remove(`${AppConstants.TEXT_SIZE.SMALL}-text-size`);

    bodyClasses.add(`${textSizeValue}-text-size`);
  }

  public aboutUsClicked(): void {
    this.appInsightsService.trackPageViewWithoutClientId(
      AppCenterAnalytics.GUEST_HOME_ABOUT_US_CLICKED
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
